import { PropsWithChildren } from "react";

import PlusIcon from "@assets/icons/plus.svg";
import CloseIcon from "@assets/icons/close.svg";

import useSystemStore from "@store/useSystemStore";
import { ITagResponse } from "@interfaces/responses.interface";
import { cn } from "@utils/tailwind.util";
import { View } from "@ui/View";

interface Props {
  data: ITagResponse;
  isDisabled: boolean;
  onAdd?: (tag: ITagResponse) => void;
  onRemove?: (tag: ITagResponse) => void;
  onRedirect?: () => void;
  withPlusIcon?: boolean;
  isMemberTag?: boolean;
  isSuggestedTag?: boolean;
}

export function TagWrapper({
  data,
  isDisabled,
  onAdd,
  onRemove,
  onRedirect,
  withPlusIcon,
  isMemberTag,
  isSuggestedTag = false,
  children,
}: PropsWithChildren<Props>) {
  const { isDarkMode } = useSystemStore();

  const getHoveredBackground = (): string => {
    switch (true) {
      case isDarkMode && !isMemberTag:
        return "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #313643) 50.41%)";
      case isDarkMode && isMemberTag:
        return "linear-gradient(90deg, rgba(56, 87, 83, 0.00) 0%, var(--tag-hover, #2A3C41) 50.41%)";
      case !isDarkMode && !isMemberTag:
        return "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #F3F4F6) 50.41%)";
      case !isDarkMode && isMemberTag:
        return "linear-gradient(90deg, rgba(138, 178, 189, 0.00) 0%, var(--tag-hover, #DCEBE9) 50.41%)";
    }
  };

  const handleClick = () => {
    if (onAdd) {
      onAdd?.(data);
    } else {
      onRedirect?.();
    }
  };

  const canBeRemoved = !isDisabled && !!onRemove && !isSuggestedTag;

  return (
    <div className="group relative max-w-full non-selectable" key={data.object.id}>
      <div
        className={cn(
          "bg-background-tag text-content-primary dark:text-white",
          "inline-flex h-7 max-w-full cursor-pointer [&_*]:!cursor-pointer items-center gap-1 rounded-md p-1 align-middle leading-none no-underline outline-none ring-1 ring-inset ring-stroke-primary transition-colors group-hover:bg-gray-100",
          "dark:bg-gray-750 dark:ring-gray-700 dark:group-hover:bg-gray-750",
          {
            "bg-background-tagMemberDefault group-hover:bg-teal-100 dark:bg-teal-950 dark:group-hover:bg-teal-950":
              isMemberTag,
            "border border-dashed border-stroke-secondary rounded-lg bg-transparent dark:bg-transparent transition-colors duration-200 hover:border-content-disabled hover:bg-[#F9F9FB] ring-0":
              isSuggestedTag,
          }
        )}
        onClick={handleClick}
      >
        {withPlusIcon && (
          <PlusIcon width={20} height={20} className="flex-shrink-0 dark:text-white" />
        )}

        {children}

        <View.Condition if={isSuggestedTag}>
          <span className="absolute right-2 top-1 z-20 flex h-5 w-4 items-center justify-end rounded leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100">
            <PlusIcon
              width={20}
              height={20}
              className="inline-block align-middle text-content-secondary dark:text-white"
            />
          </span>
          <div
            className="pointer-events-none absolute right-1 top-1 z-10 h-5 w-10 rounded opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
            style={{ background: getHoveredBackground() }}
          />
        </View.Condition>

        <View.Condition if={canBeRemoved}>
          <button
            type="button"
            className="absolute right-[6px] top-[3px] z-20 flex h-5 w-4 items-center justify-end rounded leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
            onMouseDown={(e) => {
              e.preventDefault(); // Stop native scrolling
              onRemove?.(data);
            }}
            // The tag usually unmounts on mousedown; when it survives, the click must not redirect.
            onClick={(e) => e.stopPropagation()}
          >
            <CloseIcon
              width={16}
              height={16}
              className="inline-block align-middle text-content-secondary dark:text-white"
            />
          </button>
          <div
            className="pointer-events-none absolute right-1 top-[3px] z-10 h-5 w-10 rounded opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
            style={{ background: getHoveredBackground() }}
          />
        </View.Condition>
      </div>
    </div>
  );
}
